import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import Popover from '@material-ui/core/Popover'
import Dialog from '@material-ui/core/Dialog'
import useTranslation from '../../../Hooks/useTranslation'
import { useAuth } from '../../../Contexts/AuthProvider'
import AlertToastr from '../../../utils/alert'
import MmsService from '../../../Services/MMS/mms-api.service'
import DmsService from '../../../Services/DMS/dms-api.service'
import {
  DETAIL_BLOCKS,
  loadFleetViewChannelSettings,
} from './fleetViewSettingsStorage'

const DEFAULT_FLEET_TYPE = 'fracFleet'
const DEFAULT_EQUIPMENT_TYPE = 'Pump'

/** Matches backend FleetType enum order used by /Fleet/standard-channel-fields/{fleetType}. */
const FLEET_TYPE_API_VALUES = {
  fracFleet: 0,
  pumpdownFleet: 1,
  customer: 2,
  yard: 3,
}

const CATEGORY_TO_BLOCK_KEY = {
  Engine: 'engine',
  Transmission: 'transmission',
  Pump: 'pump',
  Hydraulic: 'hydraulic',
  DGB: 'dgb',
  Table: 'table',
  Blender: 'blender',
}

const BLOCK_KEY_TO_CATEGORY = Object.entries(CATEGORY_TO_BLOCK_KEY).reduce((acc, [category, key]) => {
  acc[key] = category
  return acc
}, {})

const toFleetTypeApiValue = (fleetType) => {
  if (fleetType == null || fleetType === '') return null
  if (typeof fleetType === 'number' && Number.isFinite(fleetType)) return fleetType
  const asNumber = Number(fleetType)
  if (!Number.isNaN(asNumber) && String(fleetType).trim() !== '') return asNumber
  const key = String(fleetType)
  if (Object.prototype.hasOwnProperty.call(FLEET_TYPE_API_VALUES, key)) {
    return FLEET_TYPE_API_VALUES[key]
  }
  const match = Object.keys(FLEET_TYPE_API_VALUES).find(
    (name) => name.toLowerCase() === key.toLowerCase()
  )
  return match != null ? FLEET_TYPE_API_VALUES[match] : null
}

const categoryToBlockKey = (category) => (
  CATEGORY_TO_BLOCK_KEY[category] || String(category || '').toLowerCase()
)

const PREVIEW_PUMP = 'pump'
const PREVIEW_DETAILS = 'details'
const DEFAULT_DETAIL_CATEGORY = 'Engine'

const BLENDER_KPI_COLORS = ['#3fc6e8', '#5ea8ff', '#7c8cf0', '#46d3c0', '#4a8ef0']

const formatPreviewChannel = (sourceChannelName) => {
  const value = sourceChannelName != null ? String(sourceChannelName).trim() : ''
  return value || '{channel}'
}

const buildChannelLabelsByKey = (channels) => {
  const labelsByKey = {}
  const register = (key, label) => {
    const normalized = key != null ? String(key).trim() : ''
    const resolved = label != null ? String(label).trim() : ''
    if (!normalized || !resolved) return
    labelsByKey[normalized] = resolved
    labelsByKey[normalized.toLowerCase()] = resolved
  }
  ;(Array.isArray(channels) ? channels : []).forEach((channel) => {
    const label = channel?.label
      || channel?.nameLocalized
      || channel?.name
      || channel?.sourceChannelName
      || ''
    register(channel?.sourceChannelName, label)
    register(channel?.name, label)
    register(channel?.nameLocalized, label)
    register(channel?.code, label)
    register(channel?.SourceChannelName, label)
  })
  return labelsByKey
}

const normalizeUnitChannels = (data) => {
  const raw = Array.isArray(data?.channels)
    ? data.channels
    : (Array.isArray(data?.Channels) ? data.Channels : [])
  return raw
    .map((channel, index) => {
      const sourceChannelName = channel?.sourceChannelName
        || channel?.SourceChannelName
        || channel?.name
        || channel?.nameLocalized
        || channel?.code
        || ''
      const id = channel?.id != null
        ? String(channel.id)
        : (channel?.code != null ? String(channel.code) : `channel-${index}`)
      return {
        ...channel,
        id,
        sourceChannelName: String(sourceChannelName || '').trim(),
        label: channel?.nameLocalized
          || channel?.name
          || channel?.sourceChannelName
          || channel?.code
          || sourceChannelName
          || '—',
      }
    })
    .filter((channel) => channel.sourceChannelName)
}

const buildUnitNamesByUId = (unitNames) => {
  const namesByUId = {}
  ;(Array.isArray(unitNames) ? unitNames : []).forEach(({ uId, name }) => {
    if (!uId || !name) return
    namesByUId[uId] = name
    namesByUId[uId.toLowerCase()] = name
  })
  return namesByUId
}

const mapApiFieldToChannel = (field, index) => ({
  id: field.id != null ? String(field.id) : `api-${field.fieldName || index}`,
  fieldId: field.id,
  key: field.fieldName,
  label: field.fieldName || '',
  unit: field.unit || '',
  decimals: 1,
  isDefault: true,
  order: field.order ?? index,
})

const FleetViewChannelMappings = () => {
  const translation = useTranslation()
  const { currentCompany } = useAuth()
  const [fleetType, setFleetType] = useState(DEFAULT_FLEET_TYPE)
  const [fleetTypeOptions, setFleetTypeOptions] = useState([])
  const [standardChannelFields, setStandardChannelFields] = useState(null)
  const [equipmentType, setEquipmentType] = useState(DEFAULT_EQUIPMENT_TYPE)
  const [previewMode, setPreviewMode] = useState(PREVIEW_PUMP)
  const [category, setCategory] = useState('Table')
  const [settings, setSettings] = useState(() => loadFleetViewChannelSettings())
  const [unitPicker, setUnitPicker] = useState(null)
  const [channelMenuAnchorEl, setChannelMenuAnchorEl] = useState(null)
  const [unitChannels, setUnitChannels] = useState([])
  const [licenseOptions, setLicenseOptions] = useState([])
  const [licenseNamesById, setLicenseNamesById] = useState({})
  const [unitNamesByUId, setUnitNamesByUId] = useState({})
  const [unitOptions, setUnitOptions] = useState([])
  const [selectedLicenseId, setSelectedLicenseId] = useState('')
  const [selectedUnitUId, setSelectedUnitUId] = useState('')
  const [channelsLoading, setChannelsLoading] = useState(false)
  const [channelsSaving, setChannelsSaving] = useState(false)
  const [channelsError, setChannelsError] = useState('')
  const [channelSearch, setChannelSearch] = useState('')
  const [pendingChannelSelection, setPendingChannelSelection] = useState(null)
  const [selectedUnitsByField, setSelectedUnitsByField] = useState({})
  const [channelLabelsByKey, setChannelLabelsByKey] = useState({})

  const blockKey = useMemo(() => categoryToBlockKey(category), [category])
  const isPumpPreview = previewMode === PREVIEW_PUMP

  const getLicenseLabel = useCallback((licenseId) => {
    if (licenseId == null || licenseId === '') return ''
    const key = String(licenseId)
    return licenseNamesById[key] || key
  }, [licenseNamesById])

  const getUnitLabel = useCallback((unitUId) => {
    if (unitUId == null || unitUId === '') return ''
    const key = String(unitUId).trim()
    return unitNamesByUId[key]
      || unitNamesByUId[key.toLowerCase()]
      || key
  }, [unitNamesByUId])

  const resolvePreviewChannelLabel = useCallback((value) => {
    const raw = value != null ? String(value).trim() : ''
    if (!raw) return '{channel}'
    return channelLabelsByKey[raw]
      || channelLabelsByKey[raw.toLowerCase()]
      || unitNamesByUId[raw]
      || unitNamesByUId[raw.toLowerCase()]
      || raw
  }, [channelLabelsByKey, unitNamesByUId])

  const selectedUnitLabel = useMemo(
    () => getUnitLabel(selectedUnitUId),
    [getUnitLabel, selectedUnitUId]
  )

  useEffect(() => {
    setSettings(loadFleetViewChannelSettings())
  }, [])

  useEffect(() => {
    const companyId = currentCompany?.id
    if (!companyId) {
      setLicenseNamesById({})
      return undefined
    }

    let cancelled = false
    DmsService.getLicenseIdsByCompanyId(companyId)
      .then((response) => {
        if (cancelled) return
        const list = Array.isArray(response?.data) ? response.data : []
        const namesById = {}
        list.forEach((license) => {
          const id = license?.id != null
            ? String(license.id)
            : (license?.licenseId != null ? String(license.licenseId) : '')
          if (!id) return
          namesById[id] = license?.name || license?.licenseName || id
        })
        setLicenseNamesById(namesById)
      })
      .catch(() => {
        if (!cancelled) setLicenseNamesById({})
      })

    return () => { cancelled = true }
  }, [currentCompany?.id])

  useEffect(() => {
    let cancelled = false
    MmsService.getFleetTypesOptions()
      .then((response) => {
        if (cancelled) return
        const options = response?.data || []
        setFleetTypeOptions(options)
        const hasCurrent = options.some((option) => option.fleetType === fleetType)
        if (!hasCurrent) {
          const frac = options.find(
            (option) => String(option.fleetType || '').toLowerCase() === DEFAULT_FLEET_TYPE.toLowerCase()
          )
          setFleetType(frac?.fleetType || options[0]?.fleetType || DEFAULT_FLEET_TYPE)
        }
      })
      .catch(() => {
        if (!cancelled) setFleetTypeOptions([])
      })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const apiFleetType = toFleetTypeApiValue(fleetType)
    if (apiFleetType == null) {
      setStandardChannelFields(null)
      return undefined
    }

    let cancelled = false
    DmsService.getFleetStandardChannelFields(apiFleetType)
      .then((response) => {
        if (cancelled) return
        setStandardChannelFields(response?.data ?? null)
      })
      .catch((error) => {
        if (cancelled) return
        setStandardChannelFields(null)
        const message = error?.response?.data?.Message
          || error?.message
          || 'Failed to load standard channel fields'
        AlertToastr.showErrorAlert(message)
      })

    return () => { cancelled = true }
  }, [fleetType])

  useEffect(() => {
    let cancelled = false
    setLicenseOptions([])
    setUnitOptions([])
    setSelectedLicenseId('')
    setSelectedUnitUId('')
    setSelectedUnitsByField({})

    DmsService.getFleetChannelMappings({ equipmentType })
      .then((response) => {
        if (cancelled) return
        const filters = response?.data?.filters || {}
        const licenses = Array.isArray(filters.licenseIds) ? filters.licenseIds : []
        const units = Array.isArray(filters.unitUIds) ? filters.unitUIds : []
        setLicenseOptions(licenses)
        setUnitOptions(units)
        setUnitNamesByUId(buildUnitNamesByUId(filters.unitNames))
        setSelectedLicenseId(licenses[0] || '')
        setSelectedUnitUId(units[0] || '')
      })
      .catch(() => {
        if (cancelled) return
        setLicenseOptions([])
        setUnitOptions([])
        setSelectedLicenseId('')
        setSelectedUnitUId('')
        setSelectedUnitsByField({})
      })

    return () => { cancelled = true }
  }, [equipmentType])

  useEffect(() => {
    if (!selectedUnitUId && !selectedLicenseId) {
      setSelectedUnitsByField({})
      return undefined
    }

    let cancelled = false
    // Drop previous unit's values immediately so preview does not keep stale mappings.
    setSelectedUnitsByField({})

    const params = { equipmentType }
    if (selectedUnitUId) params.unitUId = selectedUnitUId
    if (selectedLicenseId) params.licenseId = selectedLicenseId

    DmsService.getFleetChannelMappings(params)
      .then((response) => {
        if (cancelled) return
        const mappings = Array.isArray(response?.data?.mappings) ? response.data.mappings : []
        const next = {}
        const nextLabels = {}
        mappings.forEach((mapping) => {
          const standardName = mapping?.standardName
          const sourceChannelName = mapping?.sourceChannelName
          if (!standardName) return
          // Keep only mappings for the currently selected unit/license when API returns extras.
          if (
            selectedUnitUId
            && mapping.unitUId
            && String(mapping.unitUId) !== String(selectedUnitUId)
          ) {
            return
          }
          if (
            selectedLicenseId
            && mapping.licenseId
            && String(mapping.licenseId) !== String(selectedLicenseId)
          ) {
            return
          }
          const value = sourceChannelName != null ? String(sourceChannelName).trim() : ''
          if (!value) return
          next[standardName] = value

          const localized = mapping?.sourceChannelNameLocalized
            || mapping?.nameLocalized
            || mapping?.channelNameLocalized
            || mapping?.sourceChannelNameLocal
            || ''
          if (localized) {
            const label = String(localized).trim()
            if (label) {
              nextLabels[value] = label
              nextLabels[value.toLowerCase()] = label
            }
          }
        })
        setSelectedUnitsByField(next)
        if (Object.keys(nextLabels).length) {
          setChannelLabelsByKey((prev) => ({ ...prev, ...nextLabels }))
        }
      })
      .catch(() => {
        if (!cancelled) setSelectedUnitsByField({})
      })

    return () => { cancelled = true }
  }, [equipmentType, selectedUnitUId, selectedLicenseId])

  useEffect(() => {
    if (!selectedUnitUId) {
      setChannelLabelsByKey({})
      return undefined
    }

    let cancelled = false
    DmsService.getUnitByUnitUId(selectedUnitUId)
      .then((response) => {
        if (cancelled) return
        const nextChannels = normalizeUnitChannels(response?.data ?? response)
        setChannelLabelsByKey((prev) => ({
          ...prev,
          ...buildChannelLabelsByKey(nextChannels),
        }))
      })
      .catch(() => {})

    return () => { cancelled = true }
  }, [selectedUnitUId])

  const equipmentTypeOptions = useMemo(() => {
    const fromMeta = standardChannelFields?.metadata?.equipmentTypes
    if (Array.isArray(fromMeta) && fromMeta.length) {
      return fromMeta.map((item) => ({
        value: item.item || item.name,
        label: item.name || item.item,
      })).filter((item) => item.value)
    }
    return [
      { value: 'Pump', label: translation.fleetViewSettingsPumpTypeLabel || 'Pump' },
      { value: 'Blender', label: translation.fleetViewSettingsBlenderTypeLabel || 'Blender' },
    ]
  }, [standardChannelFields, translation])

  const allCategoryOptions = useMemo(() => {
    const fields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
      : []
    const metaOrder = Array.isArray(standardChannelFields?.metadata?.categories)
      ? standardChannelFields.metadata.categories
      : []

    const forEquipment = fields
      .filter((field) => String(field.equipmentType || '') === String(equipmentType || ''))
      .map((field) => field.category)
      .filter(Boolean)

    const unique = [...new Set(forEquipment)]
    unique.sort((a, b) => {
      const ai = metaOrder.indexOf(a)
      const bi = metaOrder.indexOf(b)
      if (ai === -1 && bi === -1) return String(a).localeCompare(String(b))
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
    return unique
  }, [standardChannelFields, equipmentType])

  const categoryOptions = useMemo(() => {
    if (isPumpPreview) {
      return allCategoryOptions.filter((item) => item === 'Table')
    }
    return allCategoryOptions.filter((item) => item !== 'Table')
  }, [allCategoryOptions, isPumpPreview])

  const apiChannelsForCategory = useMemo(() => {
    const fields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
      : []
    return fields
      .filter((field) => (
        String(field.equipmentType || '') === String(equipmentType || '')
        && String(field.category || '') === String(category || '')
      ))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(mapApiFieldToChannel)
  }, [standardChannelFields, equipmentType, category])

  const isPumpEquipment = String(equipmentType || '').toLowerCase() === 'pump'

  useEffect(() => {
    if (!equipmentTypeOptions.length) return
    const hasCurrent = equipmentTypeOptions.some((option) => option.value === equipmentType)
    if (!hasCurrent) {
      const pump = equipmentTypeOptions.find(
        (option) => String(option.value).toLowerCase() === 'pump'
      )
      setEquipmentType(pump?.value || equipmentTypeOptions[0].value)
    }
  }, [equipmentTypeOptions, equipmentType])

  useEffect(() => {
    if (!isPumpEquipment) {
      const preferred = allCategoryOptions[0]
      if (preferred && category !== preferred) setCategory(preferred)
      return
    }
    if (!categoryOptions.length) return
    if (!categoryOptions.includes(category)) {
      const preferred = categoryOptions.includes(DEFAULT_DETAIL_CATEGORY)
        ? DEFAULT_DETAIL_CATEGORY
        : categoryOptions[0]
      setCategory(preferred)
    }
  }, [isPumpEquipment, categoryOptions, category, allCategoryOptions])

  const handlePreviewModeChange = (mode) => {
    setPreviewMode(mode)
    if (mode === PREVIEW_PUMP) {
      setCategory('Table')
      return
    }
    const preferred = allCategoryOptions.includes(DEFAULT_DETAIL_CATEGORY)
      ? DEFAULT_DETAIL_CATEGORY
      : allCategoryOptions.find((item) => item !== 'Table')
    if (preferred) setCategory(preferred)
  }

  // API is source of truth for order/ids when loaded (Pump Table + Details categories).
  const channels = useMemo(() => {
    if (apiChannelsForCategory.length) return apiChannelsForCategory
    const fromSettings = settings[blockKey]
    if (Array.isArray(fromSettings) && fromSettings.length) return fromSettings
    return []
  }, [settings, blockKey, apiChannelsForCategory])

  const getChannelsForBlockKey = useCallback((key) => {
    const blockCategory = BLOCK_KEY_TO_CATEGORY[key] || key
    const apiFields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
        .filter((field) => (
          String(field.equipmentType || '') === String(equipmentType || '')
          && String(field.category || '') === String(blockCategory || '')
        ))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(mapApiFieldToChannel)
      : []
    if (apiFields.length) return apiFields
    const fromSettings = settings[key]
    return Array.isArray(fromSettings) ? fromSettings : []
  }, [standardChannelFields, equipmentType, settings])

  const selectCategoryByBlockKey = useCallback((nextBlockKey) => {
    setPreviewMode(PREVIEW_DETAILS)
    setCategory(
      BLOCK_KEY_TO_CATEGORY[nextBlockKey]
      || (categoryOptions.includes(DEFAULT_DETAIL_CATEGORY) ? DEFAULT_DETAIL_CATEGORY : null)
      || categoryOptions[0]
      || DEFAULT_DETAIL_CATEGORY
    )
  }, [categoryOptions])

  const pumpTilePreview = useMemo(() => {
    const tableChannels = (Array.isArray(settings.table) && settings.table.length)
      ? [...settings.table].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : (Array.isArray(standardChannelFields?.fields)
        ? standardChannelFields.fields
          .filter((field) => (
            String(field.equipmentType || '') === 'Pump'
            && String(field.category || '') === 'Table'
          ))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(mapApiFieldToChannel)
        : [])

    const mainFields = tableChannels.slice(0, 2)
    const metricFields = tableChannels.slice(2)

    return {
      mainMetrics: mainFields.map((ch) => ({
        key: ch.key,
        label: ch.label,
        display: formatPreviewChannel(
          selectedUnitsByField[ch.key]
            ? resolvePreviewChannelLabel(selectedUnitsByField[ch.key])
            : ch.unit
        ),
      })),
      metrics: metricFields.map((ch) => ({
        key: ch.key,
        label: ch.label,
        display: formatPreviewChannel(
          selectedUnitsByField[ch.key]
            ? resolvePreviewChannelLabel(selectedUnitsByField[ch.key])
            : ch.unit
        ),
      })),
    }
  }, [settings.table, standardChannelFields, selectedUnitsByField, resolvePreviewChannelLabel])

  const blenderKpiPreview = useMemo(() => (
    channels.map((ch, index) => ({
      key: ch.key,
      label: ch.label,
      display: formatPreviewChannel(
        selectedUnitsByField[ch.key]
          ? resolvePreviewChannelLabel(selectedUnitsByField[ch.key])
          : ch.unit
      ),
      color: BLENDER_KPI_COLORS[index % BLENDER_KPI_COLORS.length],
    }))
  ), [channels, selectedUnitsByField, resolvePreviewChannelLabel])

  const handleCloseUnitPicker = () => {
    if (channelsLoading || channelsSaving) return
    setChannelMenuAnchorEl(null)
    setUnitPicker(null)
    setUnitChannels([])
    setChannelsError('')
    setChannelSearch('')
  }

  const closeUnitPickerNow = () => {
    setChannelMenuAnchorEl(null)
    setUnitPicker(null)
    setUnitChannels([])
    setChannelsError('')
    setChannelSearch('')
  }

  const filteredUnitChannels = useMemo(() => {
    const query = channelSearch.trim().toLowerCase()
    if (!query) return unitChannels
    return unitChannels.filter((channel) => {
      const hay = `${channel.sourceChannelName || ''} ${channel.label || ''}`.toLowerCase()
      return hay.includes(query)
    })
  }, [unitChannels, channelSearch])

  const handleLicenseChange = useCallback(async (licenseId) => {
    const nextLicenseId = licenseId || ''
    setSelectedLicenseId(nextLicenseId)
    try {
      const params = { equipmentType }
      if (nextLicenseId) params.licenseId = nextLicenseId
      const response = await DmsService.getFleetChannelMappings(params)
      const filters = response?.data?.filters || {}
      const units = Array.isArray(filters.unitUIds) ? filters.unitUIds : []
      setUnitOptions(units)
      setSelectedUnitUId((prev) => (units.includes(prev) ? prev : (units[0] || '')))
    } catch {
      setUnitOptions([])
      setSelectedUnitUId('')
    }
  }, [equipmentType])

  const handleOpenUnitPicker = useCallback(async (event, field) => {
    if (event?.stopPropagation) event.stopPropagation()
    if (event?.preventDefault) event.preventDefault()

    const fieldName = field?.key || field?.fieldName || ''
    if (!fieldName) return

    if (!selectedUnitUId) {
      AlertToastr.showErrorAlert(
        translation.fleetViewSettingsUnitUIdRequired || 'Select Unit uid first'
      )
      return
    }

    const anchor = event?.currentTarget || null
    setChannelMenuAnchorEl(anchor)
    setUnitPicker({
      fieldName,
      label: field.label || fieldName,
    })
    setUnitChannels([])
    setChannelsError('')
    setChannelsLoading(true)

    try {
      const response = await DmsService.getUnitByUnitUId(selectedUnitUId)
      const nextChannels = normalizeUnitChannels(response?.data ?? response)
      setUnitChannels(nextChannels)
      setChannelLabelsByKey((prev) => ({
        ...prev,
        ...buildChannelLabelsByKey(nextChannels),
      }))
      if (!nextChannels.length) {
        setChannelsError(
          translation.fleetViewSettingsNoUnitChannels
          || 'No channels found for this unit.'
        )
      }
    } catch (error) {
      setUnitChannels([])
      setChannelsError(
        error?.response?.data?.Message
        || error?.response?.data?.message
        || error?.message
        || 'Failed to load unit channels'
      )
    } finally {
      setChannelsLoading(false)
    }
  }, [selectedUnitUId, translation])

  const handleSelectChannel = async (channel) => {
    if (!unitPicker?.fieldName || !channel || channelsSaving) return

    const sourceChannelName = channel.sourceChannelName || ''
    if (!sourceChannelName) {
      AlertToastr.showErrorAlert('Cannot save: source channel name is missing')
      return
    }

    if (channel.label) {
      setChannelLabelsByKey((prev) => ({
        ...prev,
        ...buildChannelLabelsByKey([channel]),
      }))
    }

    setPendingChannelSelection({
      sourceChannelName,
      standardName: unitPicker.fieldName,
      equipmentType,
    })
  }

  const handleCloseApplyMappingDialog = () => {
    if (channelsSaving) return
    setPendingChannelSelection(null)
  }

  const handleApplyChannelMapping = async (applyToAll = false) => {
    if (!pendingChannelSelection?.standardName || !pendingChannelSelection?.sourceChannelName || channelsSaving) return

    const { sourceChannelName, standardName, equipmentType: pendingEquipmentType } = pendingChannelSelection
    setChannelsSaving(true)
    setChannelsError('')
    try {
      const params = { equipmentType: pendingEquipmentType }
      if (selectedUnitUId && !applyToAll) params.unitUId = selectedUnitUId
      if (selectedLicenseId) params.licenseId = selectedLicenseId
      const mappingsResponse = await DmsService.getFleetChannelMappings(params)
      const mappings = Array.isArray(mappingsResponse?.data?.mappings)
        ? mappingsResponse.data.mappings
        : []
      const existing = mappings.find((mapping) => (
        String(mapping.standardName || '') === String(standardName)
      ))

      if (existing?.id != null && Number.isFinite(Number(existing.id))) {
        const response = await DmsService.updateFleetChannelMapping(Number(existing.id), {
          sourceChannelName,
          standardName,
          equipmentType: existing.equipmentType || pendingEquipmentType,
          confidence: existing.confidence != null ? Number(existing.confidence) : 0,
          applyToAll,
        })
        const payload = response?.data || {}
        const isSuccessful = payload.isSuccessful ?? payload.IsSuccessful
        const message = payload.message || payload.Message
        if (isSuccessful === false) {
          AlertToastr.showErrorAlert(message || 'Failed to update channel mapping')
          return
        }
      }

      setSelectedUnitsByField((prev) => ({
        ...prev,
        [standardName]: sourceChannelName,
      }))
      setPendingChannelSelection(null)
      closeUnitPickerNow()
    } catch (error) {
      AlertToastr.showErrorAlert(
        error?.response?.data?.Message
        || error?.response?.data?.message
        || error?.message
        || 'Failed to save channel selection'
      )
    } finally {
      setChannelsSaving(false)
    }
  }

  return (
    <div className="pageCard fleetViewSettings fleetViewSettings--mappings">
      <div className="fleetViewSettingsHeader">
        <div className="fleetViewSettingsHeaderMain">
          <TextField
            select
            variant="outlined"
            margin="none"
            label={translation.saveDepartmentModalFleetTypeLabel || 'Fleet Type'}
            value={fleetType}
            onChange={(e) => setFleetType(e?.target?.value || DEFAULT_FLEET_TYPE)}
            id="fleetViewSettingsFleetTypeSelect"
            className="fleetViewSettingsTypeSelect"
          >
            {!fleetTypeOptions.length ? (
              <MenuItem value={DEFAULT_FLEET_TYPE} className="filterMenuItem">
                Frac fleet
              </MenuItem>
            ) : (
              fleetTypeOptions.map((option) => (
                <MenuItem
                  key={option.fleetType}
                  value={option.fleetType}
                  className="filterMenuItem"
                >
                  {option.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            variant="outlined"
            margin="none"
            label={translation.fleetViewSettingsTypeLabel || 'Equipment type'}
            value={equipmentType}
            onChange={(e) => setEquipmentType(e?.target?.value || DEFAULT_EQUIPMENT_TYPE)}
            id="fleetViewSettingsEquipmentTypeSelect"
            className="fleetViewSettingsTypeSelect"
          >
            {equipmentTypeOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                className="filterMenuItem"
              >
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            variant="outlined"
            margin="none"
            label={translation.fleetViewSettingsLicensesLabel || 'Licenses'}
            value={selectedLicenseId}
            onChange={(e) => handleLicenseChange(e?.target?.value || '')}
            id="fleetViewSettingsLicenseSelect"
            className="fleetViewSettingsTypeSelect"
            disabled={!licenseOptions.length}
          >
            {!licenseOptions.length ? (
              <MenuItem value="" className="filterMenuItem">
                —
              </MenuItem>
            ) : (
              licenseOptions.map((id) => (
                <MenuItem key={id} value={id} className="filterMenuItem">
                  {getLicenseLabel(id)}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            variant="outlined"
            margin="none"
            label={translation.fleetViewSettingsUnitUIdLabel || 'Unit uid'}
            value={selectedUnitUId}
            onChange={(e) => setSelectedUnitUId(e?.target?.value || '')}
            id="fleetViewSettingsUnitUIdSelect"
            className="fleetViewSettingsTypeSelect"
            disabled={!unitOptions.length}
            SelectProps={{
              renderValue: (value) => getUnitLabel(value),
            }}
          >
            {!unitOptions.length ? (
              <MenuItem value="" className="filterMenuItem">
                —
              </MenuItem>
            ) : (
              unitOptions.map((id) => (
                <MenuItem key={id} value={id} className="filterMenuItem">
                  {getUnitLabel(id)}
                </MenuItem>
              ))
            )}
          </TextField>
          {isPumpEquipment ? (
            <div className={isPumpPreview ? 'optionsSwitch' : 'optionsSwitch in'}>
              <div
                className="option"
                onClick={() => handlePreviewModeChange(PREVIEW_PUMP)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handlePreviewModeChange(PREVIEW_PUMP)
                }}
                id="fleetViewSettingsPreviewPumpOption"
              >
                {translation.fleetViewSettingsPreviewModeLabel || 'Preview'}
              </div>
              <div
                className="option"
                onClick={() => handlePreviewModeChange(PREVIEW_DETAILS)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handlePreviewModeChange(PREVIEW_DETAILS)
                }}
                id="fleetViewSettingsPreviewDetailsOption"
              >
                {translation.popoverDetailsLabel || 'Details'}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="fleetViewSettingsLayout fleetViewSettingsLayout--previewOnly">
        <div className="fleetViewSettingsPreviewPanel">
          <div className="fleetViewSettingsSectionHead">
            <span className="fleetViewSettingsSectionTitle">
              {!isPumpEquipment
                ? (translation.fleetViewSettingsBlenderPreviewLabel || 'Blender process preview')
                : isPumpPreview
                  ? (translation.fleetViewSettingsPumpPreviewLabel || 'Pump tile preview')
                  : (translation.fleetViewSettingsPreviewLabel || 'Pump detail preview')}
            </span>
          </div>

          {!isPumpEquipment ? (
            <div className="fleetViewSettingsPreviewCard fleetViewSettingsBlenderPreview">
              <div className="fdCardToolbar">
                <span className="fdCardToolbarTitle">
                  {translation.fleetProcessTreatmentLabel || 'Process Treatment — live'}
                </span>
              </div>
              {!blenderKpiPreview.length ? (
                <div className="fleetViewSettingsEmpty">
                  {translation.fleetViewSettingsNoChannels
                    || 'No channels. Add a channel or reset to defaults.'}
                </div>
              ) : (
                <ul className="digitalDisplayList">
                  {blenderKpiPreview.map((kpi) => (
                    <li className="digitalDisplayListItem" key={kpi.key}>
                      <h3 className="name">{kpi.label}</h3>
                      <div className="displayContent">
                        <div className="data">
                          <button
                            type="button"
                            className="qnt fleetViewSettingsUnitBtn"
                            onClick={(e) => handleOpenUnitPicker(e, kpi)}
                          >
                            {kpi.display}
                          </button>
                        </div>
                        <div className="statusbar" style={{ backgroundColor: kpi.color }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : isPumpPreview ? (
            <div className="fleetViewSettingsPreviewCard">
              <div className="fdPumpTile">
                <div className="fdPumpTileTop">
                  <span className="fdDot" style={{ background: '#46d39a' }} />
                  <div className="fdPumpTileTopText">
                    <span className="fdPumpId">{selectedUnitLabel || selectedUnitUId || 'Pump'}</span>
                    {selectedUnitUId ? (
                      <span className="fdPumpTrans">{selectedUnitUId}</span>
                    ) : null}
                  </div>
                </div>
                <div className="fdPumpMain">
                  {pumpTilePreview.mainMetrics.map((metric, index) => (
                    <div
                      key={metric.key}
                      className={`fdPumpMainRow${index === 0 ? ' fdPumpMainRow--primary' : ''}`}
                    >
                      <span className="fdPumpMainLabel" title={metric.label}>{metric.label}</span>
                      <button
                        type="button"
                        className="fdPumpMainValue fleetViewSettingsUnitBtn"
                        title={metric.display}
                        onClick={(e) => handleOpenUnitPicker(e, metric)}
                      >
                        {metric.display}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="fdPumpMetrics">
                  {pumpTilePreview.metrics.map((metric) => (
                    <div key={metric.key} className="fdPumpMetricRow">
                      <span title={metric.label}>{metric.label}</span>
                      <button
                        type="button"
                        className="fleetViewSettingsUnitBtn"
                        title={metric.display}
                        onClick={(e) => handleOpenUnitPicker(e, metric)}
                      >
                        {metric.display}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="fleetViewSettingsDetailPreview">
              <div className="fleetViewSettingsDetailPreviewHead">
                <span className="fleetViewSettingsDetailPreviewTitle">{selectedUnitLabel || selectedUnitUId || 'Pump'}</span>
                <span className="fleetViewSettingsDetailPreviewSub">{selectedUnitUId || ''}</span>
              </div>
              <div className="fleetViewSettingsDetailPreviewBody">
                <div className="fleetViewSettingsDetailPreviewGrid">
                  {DETAIL_BLOCKS.filter((b) => b.key !== 'dgb').map((block) => {
                    const fields = getChannelsForBlockKey(block.key)
                    const isActive = block.key === blockKey
                    return (
                      <div
                        key={block.key}
                        className={`fdGroupCard fleetViewSettingsPreviewBlock${isActive ? ' is-active' : ''}`}
                        onClick={() => selectCategoryByBlockKey(block.key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') selectCategoryByBlockKey(block.key)
                        }}
                      >
                        <div className="fdGroupCardTitle">
                          {block.title}
                        </div>
                        {fields.map((field) => (
                          <div key={field.id || field.key} className="fdGroupCardRow">
                            <span>{field.label}</span>
                            <button
                              type="button"
                              className="fleetViewSettingsUnitBtn"
                              onClick={(e) => handleOpenUnitPicker(e, field)}
                            >
                              {formatPreviewChannel(
                                selectedUnitsByField[field.key]
                                  ? resolvePreviewChannelLabel(selectedUnitsByField[field.key])
                                  : field.unit
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
                {(() => {
                  const dgb = DETAIL_BLOCKS.find((b) => b.key === 'dgb')
                  const fields = getChannelsForBlockKey('dgb')
                  const isActive = blockKey === 'dgb'
                  return (
                    <div
                      className={`fdDgb fleetViewSettingsPreviewBlock${isActive ? ' is-active' : ''}`}
                      onClick={() => selectCategoryByBlockKey('dgb')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') selectCategoryByBlockKey('dgb')
                      }}
                    >
                      <div className="fdGroupCardTitle">
                        {dgb.title}
                      </div>
                      <div className="fdDgbGrid">
                        {fields.map((field) => (
                          <div key={field.id || field.key} className="fdGroupCardRow">
                            <span>{field.label}</span>
                            <button
                              type="button"
                              className="fleetViewSettingsUnitBtn"
                              onClick={(e) => handleOpenUnitPicker(e, field)}
                            >
                              {formatPreviewChannel(
                                selectedUnitsByField[field.key]
                                  ? resolvePreviewChannelLabel(selectedUnitsByField[field.key])
                                  : field.unit
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      <Popover
        open={Boolean(channelMenuAnchorEl)}
        anchorEl={channelMenuAnchorEl}
        onClose={handleCloseUnitPicker}
        disableScrollLock
        elevation={1}
        className="pmPopover fleetViewSettingsChannelPopover"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ style: { minWidth: 220 } }}
      >
        <div
          className="fleetViewSettingsChannelSearch"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            placeholder={translation.dashboardSearchChannelLabel || 'Search Channel'}
            value={channelSearch}
            onChange={(e) => setChannelSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={channelsLoading || channelsSaving}
            inputProps={{ 'aria-label': translation.dashboardSearchChannelLabel || 'Search Channel' }}
          />
        </div>
        <div className="fleetViewSettingsChannelMenuList">
          {channelsLoading || channelsSaving ? (
            <MenuItem disabled className="fleetViewSettingsChannelMenuItem">
              {channelsSaving
                ? (translation.savingLabel || 'Saving…')
                : (translation.loadingLabel || 'Loading…')}
            </MenuItem>
          ) : null}
          {!channelsLoading && !channelsSaving && channelsError ? (
            <MenuItem disabled className="fleetViewSettingsChannelMenuItem">
              {channelsError}
            </MenuItem>
          ) : null}
          {!channelsLoading && !channelsSaving && !channelsError && !unitChannels.length ? (
            <MenuItem disabled className="fleetViewSettingsChannelMenuItem">
              {translation.fleetViewSettingsNoUnitChannels || 'No channels found for this unit.'}
            </MenuItem>
          ) : null}
          {!channelsLoading && !channelsSaving && !channelsError && unitChannels.length && !filteredUnitChannels.length ? (
            <MenuItem disabled className="fleetViewSettingsChannelMenuItem">
              {translation.fleetViewSettingsNoUnitChannels || 'No channels found for this unit.'}
            </MenuItem>
          ) : null}
          {!channelsLoading && !channelsSaving && !channelsError
            ? filteredUnitChannels.map((channel) => (
              <MenuItem
                key={String(channel.id)}
                className="fleetViewSettingsChannelMenuItem"
                disabled={channelsSaving}
                onClick={() => handleSelectChannel(channel)}
              >
                {channel.label || channel.sourceChannelName || '—'}
              </MenuItem>
            ))
            : null}
        </div>
      </Popover>

      <Dialog
        open={Boolean(pendingChannelSelection)}
        onClose={handleCloseApplyMappingDialog}
        className="respModal autoHeight"
        disableRestoreFocus
      >
        <div className="modalTitleHolder mb16">
          <h1 className="modalTitle">
            {translation.fleetViewSettingsApplyMappingTitle || 'Apply mapping'}
          </h1>
        </div>
        <div className="mb16">
          {translation.fleetViewSettingsApplyMappingPrompt
            || `Would you like to apply this mapping only to the current unit, or to all ${pendingChannelSelection?.equipmentType || equipmentType} units in the company?`}
        </div>
        <div className="btnHolder end mt22 fixedWidthBtn">
          <Button
            onClick={handleCloseApplyMappingDialog}
            variant="outlined"
            disabled={channelsSaving}
          >
            {translation.cancelButtonLabel || 'Cancel'}
          </Button>
          <Button
            onClick={() => handleApplyChannelMapping(false)}
            variant="outlined"
            color="primary"
            disabled={channelsSaving}
          >
            {translation.fleetViewSettingsApplyCurrentLabel || 'Apply to Current'}
          </Button>
          <Button
            onClick={() => handleApplyChannelMapping(true)}
            color="primary"
            variant="contained"
            disabled={channelsSaving}
          >
            {translation.fleetViewSettingsApplyAllLabel || 'Apply to All'}
          </Button>
        </div>
      </Dialog>
    </div>
  )
}

export default FleetViewChannelMappings
